import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import throttle from 'lodash/throttle';
import noop from 'lodash/noop';

import Tab from '../../../components/Tab';
import Majors from './Majors';
import Minors from './Minors';
import {
  filterMajorsByKeyword,
  filterMinorsByKeyword,
  filterMajorsByDegreeOffering,
  getAvailableDegreeOfferings,
} from '../../../utils/collections';
import withHostContext, {
  trackClickTypes,
  trackingLocations,
  trackView,
} from '../../../context/host/consumer';

import './styles.css';

const messages = defineMessages({
  majors: {
    id: 'hubs.majors-minors.majors',
    defaultMessage: 'Majors',
  },
  minors: {
    id: 'hubs.majors-minors.minors',
    defaultMessage: 'Minors',
  },
  majorsAndMinors: {
    id: 'hubs.majors-minors.majors-and-minors',
    defaultMessage: 'Majors and Minors',
  },
  offered: {
    id: 'hubs.majors-minors.offered',
    defaultMessage: ' Offered',
  },
  subHeading: {
    id: 'hubs.majors-minors.sub-heading',
    defaultMessage:
      "You can visit this institution's website for more detailed information about specific majors.",
  },
  degreeOfferings: {
    id: 'hubs.majors-minors.degree-offerings',
    defaultMessage: 'Degree Offerings:',
  },
  searchByKeyword: {
    id: 'hubs.majors-minors.search-by-keyword',
    defaultMessage: 'Search by Keyword:',
  },
  searchPlaceholderMajors: {
    id: 'hubs.majors-minors.search-placeholder-majors',
    defaultMessage: 'Name of major or area of study',
  },
  searchPlaceholderMinor: {
    id: 'hubs.majors-minors.search-placeholder-minors',
    defaultMessage: 'Name of minor',
  },
  footer: {
    id: 'hubs.majors-minors.footer',
    defaultMessage: 'Latest available data from ',
  },
});

class MajorsMinors extends React.PureComponent {
  static propTypes = {
    majors: PropTypes.arrayOf(PropTypes.object),
    lastUpdatedMajor: PropTypes.number,
    minors: PropTypes.array,
    getDegreesOfferedFilter: PropTypes.func,
    setDegreesOfferedFilter: PropTypes.func,
  };

  static defaultProps = {
    majors: [],
    minors: [],
    getDegreesOfferedFilter: noop,
    setDegreesOfferedFilter: noop,
  };

  constructor(props) {
    super(props);

    this.state = {
      activeIndex: 0,
      hasMajors: props.majors.length > 0,
      hasMinors: props.minors.length > 0,
      majorsKeywordFilter: '',
      minorsKeywordFilter: '',
      degreesOfferedFilter: props.getDegreesOfferedFilter(),
    };
  }

  handleTabChange = (e, { activeIndex }) => {
    this.setState({
      activeIndex,
    });
  };

  handleTabKeyPress = (e) => {
    /* istanbul ignore else */
    if (e.key === 'Enter') {
      const index = e.target.getAttribute('data-index');

      this.setState({ activeIndex: index });

      setTimeout(() => {
        document.activeElement.classList.add('focus-visible');
      });
    }
  };

  handleKeywordChange = (inputType, value) => {
    this.setState({
      [`${inputType}KeywordFilter`]: value,
    });
  };

  throttledKeywordChange = throttle(
    (inputType, value) => this.handleKeywordChange(inputType, value),
    500,
  );

  throttleKeywordChange = ({ target }) => {
    const inputType = target.getAttribute('data-type');
    const { value } = target;

    this.throttledKeywordChange(inputType, value);
  };

  throttledTrackKeywordSearch = throttle(({ target }) => {
    const inputType = target.getAttribute('data-type');
    const { value } = target;
    if (inputType === 'majors' && value && value.length > 1) {
      trackView(this.props.hostContext, {
        type: trackClickTypes.HUBS_VIEW,
        location: `${trackingLocations.MAJORS_SEARCH}_${value.slice(0, 10)}`,
      });
    }
  }, 1500);

  handleDegreeOfferingChange = (degreesOfferedFilter) => {
    trackView(this.props.hostContext, {
      type: trackClickTypes.HUBS_VIEW,
      location: `${trackingLocations.DEGREE_OFFERINGS}_${degreesOfferedFilter}`,
    });

    this.setState({
      degreesOfferedFilter,
    });

    this.props.setDegreesOfferedFilter(degreesOfferedFilter);
  };

  getTitle = () => {
    const { intl } = this.props;
    const { hasMajors, hasMinors } = this.state;
    const suffix = intl.formatMessage(messages.offered);

    if (hasMajors && hasMinors)
      return `${intl.formatMessage(messages.majorsAndMinors)}${suffix}`;
    if (hasMajors && !hasMinors) return `${intl.formatMessage(messages.majors)}${suffix}`;
    /* istanbul ignore else */
    if (!hasMajors && hasMinors) return `${intl.formatMessage(messages.minors)}${suffix}`;
  };

  getMenuItem = (tabName, selected, index, message) => {
    return {
      content: this.props.intl.formatMessage(message),
      role: 'tab',
      'aria-label': tabName,
      'aria-selected': selected,
      tabIndex: 0,
      onKeyPress: this.handleTabKeyPress,
      'data-index': index,
      key: `${tabName}_${index}`,
    };
  };

  getPanes = () => {
    const { majorsKeywordFilter, minorsKeywordFilter, degreesOfferedFilter } = this.state;
    const { majors, minors, lastUpdatedMajor, intl } = this.props;
    const { hasMajors, hasMinors, activeIndex } = this.state;
    const panes = [];

    let isMajorsSelected = false;

    if (hasMajors) {
      let filteredMajors = majors;

      if (degreesOfferedFilter) {
        filteredMajors = filterMajorsByDegreeOffering(
          filteredMajors,
          degreesOfferedFilter,
        );
      }

      if (majorsKeywordFilter) {
        filteredMajors = filterMajorsByKeyword(filteredMajors, majorsKeywordFilter);
      }

      const degreeFilterOptions = getAvailableDegreeOfferings(majors);

      /* istanbul ignore else */
      if (activeIndex === 0) {
        isMajorsSelected = true;
      }

      const majorLabels = {
        degreeOfferings: intl.formatMessage(messages.degreeOfferings),
        searchByKeyword: intl.formatMessage(messages.searchByKeyword),
        searchPlaceholder: intl.formatMessage(messages.searchPlaceholderMajors),
        footer: intl.formatMessage(messages.footer),
      };

      panes.push({
        menuItem: this.getMenuItem('Majors', isMajorsSelected, 0, messages.majors),
        render: () => (
          <Majors
            majors={filteredMajors}
            lastUpdatedMajor={lastUpdatedMajor}
            labels={majorLabels}
            searchInputValue={majorsKeywordFilter}
            degreesOfferedFilter={degreesOfferedFilter}
            degreeFilterOptions={degreeFilterOptions}
            onKeywordChange={(e) => {
              e.persist();
              this.throttleKeywordChange(e);
              this.throttledTrackKeywordSearch(e);
            }}
            onDegreeOfferingChange={this.handleDegreeOfferingChange}
          />
        ),
      });
    }

    if (hasMinors) {
      let filteredMinors = minors;

      if (minorsKeywordFilter) {
        filteredMinors = filterMinorsByKeyword(filteredMinors, minorsKeywordFilter);
      }

      if (hasMajors && activeIndex === 0) {
        isMajorsSelected = true;
      }

      const minorLabels = {
        searchByKeyword: intl.formatMessage(messages.searchByKeyword),
        searchPlaceholder: intl.formatMessage(messages.searchPlaceholderMinor),
      };

      panes.push({
        menuItem: this.getMenuItem(
          'Minors',
          !isMajorsSelected,
          hasMajors ? 1 : 0,
          messages.minors,
        ),
        render: () => (
          <Minors
            minors={filteredMinors}
            labels={minorLabels}
            searchInputValue={minorsKeywordFilter}
            onKeywordChange={this.throttleKeywordChange}
          />
        ),
      });
    }

    return panes;
  };

  render() {
    const { intl } = this.props;
    const { hasMajors, hasMinors, activeIndex } = this.state;

    return hasMajors || hasMinors ? (
      <div
        className="majors-minors-container common-full-width"
        data-cy="majors-minors-container"
      >
        <div className="common-core-width">
          <h2 className="heading" tabIndex={0}>
            {this.getTitle()}
          </h2>
          {hasMajors && (
            <div className="sub-heading" tabIndex={0}>
              {intl.formatMessage(messages.subHeading)}
            </div>
          )}
          <div className="tabs-container">
            <Tab
              menu={{
                secondary: true,
                pointing: true,
                role: 'tablist',
              }}
              panes={this.getPanes()}
              onTabChange={this.handleTabChange}
              activeIndex={activeIndex}
            />
          </div>
        </div>
      </div>
    ) : null;
  }
}

export default injectIntl(withHostContext(MajorsMinors));
